type Framework = 'react' | 'vue' | 'svelte' | 'solid'

/**
 * Convert an icon name to a PascalCase component name with "Icon" suffix.
 * e.g., "arrow-right" → "ArrowRightIcon"
 */
export function toComponentName(name: string): string {
	const pascalCase = name
		.split(/[-_\s]/)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join('')

	return pascalCase + 'Icon'
}

/**
 * Generate a framework component string from an SVG and icon name.
 */
export function generateComponent(
	svg: string,
	iconName: string,
	framework: Framework,
): string {
	const componentName = toComponentName(iconName)
	const cleanSvg = svg.replace(/<\?xml[^>]*\?>/g, '').trim()

	switch (framework) {
		case 'react':
			return generateReactComponent(componentName, cleanSvg)
		case 'vue':
			return generateVueComponent(componentName, cleanSvg)
		case 'svelte':
			return generateSvelteComponent(cleanSvg)
		case 'solid':
			return generateSolidComponent(componentName, cleanSvg)
	}
}

function generateReactComponent(name: string, svg: string): string {
	const reactSvg = svg
		.replace(/class=/g, 'className=')
		.replace(/clip-path=/g, 'clipPath=')
		.replace(/fill-rule=/g, 'fillRule=')
		.replace(/stroke-width=/g, 'strokeWidth=')
		.replace(/stroke-linecap=/g, 'strokeLinecap=')
		.replace(/stroke-linejoin=/g, 'strokeLinejoin=')

	return `import type { SVGProps } from "react";

export const ${name} = (props: SVGProps<SVGSVGElement>) => (
  ${reactSvg.replace('<svg', '<svg {...props}')}
);`
}

function generateVueComponent(name: string, svg: string): string {
	const vueSvg = svg
		.replace(/`/g, '\\`')
		.replace(/\$\{/g, '\\${')
		.replace('<svg', '<svg v-bind="$attrs"')
	return `export const ${name} = {
  name: "${name}",
  inheritAttrs: false,
  template: \`${vueSvg}\`,
};`
}

function generateSvelteComponent(svg: string): string {
	return svg.replace('<svg', '<svg {...$$restProps}')
}

function generateSolidComponent(name: string, svg: string): string {
	return `import type { JSX } from "solid-js";

export const ${name} = (props: JSX.IntrinsicElements["svg"]) => (
  ${svg.replace('<svg', '<svg {...props}')}
);`
}
